'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import FilmCarousel from './FilmCarousel';
import { tokens } from '../theme';

/* The film touches ResizeObserver, document.fonts and Image() on mount, so it
   is client-only. Loading it lazily also keeps it out of the first paint. */
const Film = dynamic(() => import('./film/scenes'), { ssr: false, loading: () => null });

/* The zoom from full screen into the hero slot. This is the product's opening
   move, so it always animates at full length — "Skip intro" and ?intro=0 are
   the ways past it. Deliberately NOT shortened under reduced motion: doing so
   made the landing read as an instant cut. */
const SETTLE_MS = 1700;
const HOLD_MS = 5000;   // let the closing frame be read before it shrinks

/* Fired when the film's closing CTA sends you to the form, so the section can
   play its arrival. A DOM event rather than props: `sections` reaches this
   component already rendered, so there is nothing to pass state down through. */
export const EARLY_EVENT = 'adzilio:early';

const scrollToEarly = () => {
  const el = document.getElementById('early');
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });

  /* Fire once the scroll has actually settled, not on a guessed delay — the
     journey is instant when it is already near the section and ~500ms when it
     is not, and a fixed timer plays the animation before arrival in the second
     case. `scrollend` covers both; the timer is the fallback for browsers
     without it and for a scroll that never moves at all. */
  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    window.removeEventListener('scrollend', fire);
    window.clearTimeout(timer);
    window.dispatchEvent(new CustomEvent(EARLY_EVENT));
  };
  const timer = window.setTimeout(fire, 800);
  window.addEventListener('scrollend', fire);
};

export default function CinematicShell({ hero, sections, footer }) {
  /* 'intro'    film covers the viewport, page locked
     'hold'     film has ended and rests on its final frame
     'settling' film is shrinking into the hero slot, chrome arriving
     'site'     ordinary scrollable page, film looping as a carousel      */
  const [phase, setPhase] = React.useState('intro');
  const [transform, setTransform] = React.useState(null);
  const [sceneMeta, setSceneMeta] = React.useState(null);

  const slotRef = React.useRef(null);
  const frameRef = React.useRef(null);
  const settleTimer = React.useRef(null);
  const controllerRef = React.useRef(null);

  const cinematic = phase === 'intro' || phase === 'hold' || phase === 'settling';
  const fullBleed = phase === 'intro' || phase === 'hold';
  const arriving = phase === 'settling' || phase === 'site';

  /* --- measure the hero slot and work out what covers the viewport --- */
  const measure = React.useCallback(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const r = slot.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const scale = Math.min(vw / r.width, vh / r.height);
    setTransform(
      `translate(${vw / 2 - (r.left + r.width / 2)}px, ${vh / 2 - (r.top + r.height / 2)}px) scale(${scale})`
    );
  }, []);

  React.useLayoutEffect(() => {
    if (phase !== 'intro' && phase !== 'hold') return undefined;
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [phase, measure]);

  /* ?intro=0 skips the opening outright; ?intro=1 forces it. */
  React.useEffect(() => {
    if (new URLSearchParams(window.location.search).get('intro') === '0') {
      setTransform(null);
      setPhase('site');
    }
  }, []);

  /* --- scroll lock, and always begin at the top --- */
  React.useEffect(() => {
    const locked = phase !== 'site';
    document.body.dataset.locked = locked ? 'true' : 'false';
    if (locked) window.scrollTo(0, 0);
    return () => { document.body.dataset.locked = 'false'; };
  }, [phase]);

  const settle = React.useCallback((then) => {
    setPhase((p) => (p === 'site' ? p : 'settling'));
    /* Two frames: the first lets `settling` put the transition into the
       computed style, the second releases the transform against it. Change
       both in one frame and the browser has nothing to interpolate from. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTransform(null));
    });
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      setPhase('site');
      if (typeof then === 'function') then();
    }, SETTLE_MS + 120);
  }, []);

  const onFilmComplete = React.useCallback(() => {
    /* Every scene fades out across its last 4.5%, so the true final frame is
       blank. Step back a beat so the hold rests on the resolve, not on black. */
    const c = controllerRef.current;
    if (c) c.seek(Math.max(0, c.getDuration() - 0.35));
    setPhase((p) => (p === 'intro' ? 'hold' : p));
  }, []);

  React.useEffect(() => {
    if (phase !== 'hold') return undefined;
    const t = setTimeout(() => settle(), HOLD_MS);
    return () => clearTimeout(t);
  }, [phase, settle]);

  React.useEffect(() => () => clearTimeout(settleTimer.current), []);

  /* Once the page is live the film stops being a one-shot opening and becomes
     the looping carousel. setLoop rather than a prop: SceneStage latches its
     playback value on mount and ignores later prop changes. */
  React.useEffect(() => {
    if (phase !== 'site' || !sceneMeta) return;
    const c = controllerRef.current;
    if (!c) return;
    c.setLoop(true);
    c.play();
  }, [phase, sceneMeta]);

  /* A 71-second animation running at 60fps behind a form nobody is looking at
     is pure battery. Stop it whenever the film leaves the viewport. */
  React.useEffect(() => {
    if (phase !== 'site' || !sceneMeta) return undefined;
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        const c = controllerRef.current;
        if (!c) return;
        if (entry.isIntersecting) c.play();
        else c.pause();
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [phase, sceneMeta]);

  /* the film's closing CTA */
  const goToEarlyAccess = React.useCallback(() => {
    if (phase === 'site') scrollToEarly();
    else settle(scrollToEarly);
  }, [phase, settle]);

  const settleTransition = [
    `transform ${SETTLE_MS}ms ${tokens.easeSettle}`,
    `border-radius ${SETTLE_MS}ms ${tokens.easeSettle}`,
    `box-shadow ${SETTLE_MS}ms ${tokens.easeSettle}`,
  ].join(',');

  return (
    <>
      {/* everything behind the film while it plays */}
      <Box
        aria-hidden
        sx={{
          position: 'fixed', inset: 0, background: tokens.navy, zIndex: 50,
          pointerEvents: 'none',
          opacity: cinematic ? 1 : 0,
          transition: cinematic
            ? `opacity 400ms ${tokens.ease}`
            : `opacity 1100ms ${tokens.ease} 200ms`,
        }}
      />

      <Box component="main">
        <Box component="section" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 3, md: 5 } }}>
          <Container>
            {/* the words arrive DURING the zoom, so the site lands with the film */}
            <Box
              data-hero
              sx={{
                opacity: arriving ? 1 : 0,
                transform: arriving ? 'none' : 'translateY(18px)',
                transition: `opacity 700ms ${tokens.ease} 1000ms, transform 700ms ${tokens.ease} 1000ms`,
              }}
            >
              {hero}
            </Box>

            {/* slotRef measures the FILM ALONE — the carousel row below must stay
                outside it, or the zoom scales the film to fit film + dots */}
            <Box ref={slotRef} sx={{ position: 'relative', mt: { xs: 3, md: 4 } }}>
              <Box
                ref={frameRef}
                className="film-frame"
                sx={{
                  position: 'relative', zIndex: 60, width: '100%', aspectRatio: '16/9',
                  overflow: 'hidden', background: tokens.navy, transformOrigin: 'center center',
                  borderRadius: fullBleed ? 0 : '18px',
                  boxShadow: fullBleed ? 'none' : tokens.shadowUp,
                  transform: transform || 'none',
                  /* the opening frame must SNAP to full screen, never travel to it */
                  transition: phase === 'intro' ? 'none' : settleTransition,
                }}
              >
                {/* +44px and pulled up half of it cancels the space the engine
                    reserves for its hidden playback bar, so the canvas fills the
                    frame exactly. !important because the engine sets inset:0
                    inline on its own root. Scoped to the canvas so it never
                    touches anything layered above it. */}
                <Box
                  sx={{
                    position: 'absolute', inset: 0,
                    '& > *': {
                      position: 'absolute !important', left: '0 !important', top: '-22px !important',
                      width: '100% !important', height: 'calc(100% + 44px) !important',
                    },
                  }}
                >
                  <Film
                    playbackMode="once"
                    controllerRef={controllerRef}
                    onComplete={onFilmComplete}
                    onPrimary={goToEarlyAccess}
                    onReady={setSceneMeta}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                opacity: arriving ? 1 : 0,
                transition: `opacity 600ms ${tokens.ease} 1150ms`,
                pointerEvents: arriving ? 'auto' : 'none',
              }}
            >
              <FilmCarousel
                controllerRef={controllerRef}
                scenes={sceneMeta?.scenes}
                offsets={sceneMeta?.offsets}
                active={arriving}
              />
            </Box>

            {(phase === 'intro' || phase === 'hold') && (
              <Button
                onClick={() => settle()}
                size="small"
                sx={{
                  position: 'fixed', zIndex: 70,
                  right: { xs: 16, md: 32 }, bottom: { xs: 16, md: 32 },
                  color: 'rgba(237,231,255,.9)', background: 'rgba(255,255,255,.08)',
                  border: '1px solid rgba(168,120,255,.3)',
                  '&:hover': { background: 'rgba(255,255,255,.16)' },
                }}
              >
                {phase === 'hold' ? 'Enter the site' : 'Skip intro'}
              </Button>
            )}
          </Container>
        </Box>

        {sections}
      </Box>

      {footer}
    </>
  );
}
